import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Copy, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const CourseTable = ({ 
  courses, 
  canEdit, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onDownloadPDF 
}) => {
  const navigate = useNavigate();

  if (courses.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-8 text-center text-slate-500">
          No hay cursos disponibles
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="courses-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Nº Registro
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Horas
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Fechas
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {courses.map((course) => (
              <tr
                key={course.id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/courses/${course.id}`)}
                data-testid={`course-row-${course.id}`}
              >
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-slate-900">{course.nombre}</p>
                    {course.descripcion && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-1">{course.descripcion}</p>
                    )}
                    {course.online_course_id && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Blended Learning
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {course.numero_registro ? (
                    <span className="font-mono text-sm font-medium text-[#004E89]">{course.numero_registro}</span>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-700">{course.horas}h</td>
                <td className="px-6 py-4 text-sm text-slate-700">
                  {course.fecha_inicio && course.fecha_fin
                    ? `${course.fecha_inicio} - ${course.fecha_fin}`
                    : '-'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadPDF(course)}
                      title="Descargar PDF informativo"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`course-pdf-${course.id}`}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(course)}
                          data-testid={`course-edit-${course.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicate(course.id)}
                          data-testid={`course-duplicate-${course.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(course.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`course-delete-${course.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourseTable;
